import { TimeWindowPeriod } from '@microservices/gantt-react-component'
import { EmptyObject } from '@reduxjs/toolkit'

import { getEnrichedParams, transformResponseWithPagination } from '@helpers'
import { URLS } from '@constants'
import {
  ExtendedExecuteCommand,
  FetchGanttChartBacklogDataArgs,
  FetchGanttChartConfigArgs,
  FetchGanttChartDataArgs,
  FetchGanttChartDataResponse,
  FetchGanttChartDataTransformResponse,
  FetchResourceDataArgs,
  FetchSeveralTimeLinesDataArgs,
  FetchTimeLineDataArgs,
  GanttType,
  MoveBacklogGanttBoxArgs,
  MoveGanttBoxArgs,
  ResizeGanttBoxArgs,
} from '@types'

import { GanttFormValuesBack } from '@gantt/components/GanttCreateOrEdit'
import { transformFetchGanttByIdResponse } from '@gantt/helpers'

import { commonAPI } from './common.api'

export const ganttChartApi = commonAPI.injectEndpoints({
  endpoints: build => ({
    fetchGanttChartData: build.query<FetchGanttChartDataTransformResponse, FetchGanttChartDataArgs>(
      {
        query: ({ sort, filters, configId, ...rest }) => ({
          url:
            URLS.FRONT_API +
            `/data/gantChart/${configId}?${getEnrichedParams(['sort'], [sort || undefined])}`,
          method: 'POST',
          body: { filters },
          params: rest,
        }),
        extraOptions: {
          showNotification: false,
        },
        transformResponse: transformResponseWithPagination<FetchGanttChartDataResponse>,
      }
    ),
    fetchGanttChartBacklog: build.query<
      FetchGanttChartDataResponse,
      FetchGanttChartBacklogDataArgs
    >({
      query: ({ filters, configId, ...rest }) => ({
        url: URLS.FRONT_API + `/data/gantChart/backlog/${configId}`,
        method: 'POST',
        body: { filters },
        params: rest,
      }),
      extraOptions: {
        showNotification: false,
      },
    }),
    fetchGanttChartConfig: build.query<GanttFormValuesBack, FetchGanttChartConfigArgs>({
      query: ({ configId, ...rest }) => ({
        url: URLS.FRONT_CFG + `/gantChart/config/${configId}`,
        params: rest,
      }),
      extraOptions: {
        showNotification: false,
      },
      transformResponse: transformFetchGanttByIdResponse,
    }),
    fetchGanttChart: build.query<GanttType[], EmptyObject>({
      query: () => ({
        url: URLS.FRONT_CFG + `/gantChart/all`,
      }),
      extraOptions: {
        showNotification: false,
      },
    }),
    fetchTimeLineData: build.mutation<FetchGanttChartDataResponse, FetchTimeLineDataArgs>({
      query: ({ configId, objectId, objectCode, contextId }) => ({
        url: URLS.FRONT_API + `/data/gantChart/${configId}/timeline/${objectId}`,
        method: 'POST',
        params: { objectCode, contextId },
      }),
      extraOptions: {
        showNotification: false,
      },
    }),
    fetchSeveralTimeLinesData: build.mutation<
      FetchGanttChartDataResponse,
      FetchSeveralTimeLinesDataArgs
    >({
      query: ({ configId, timelineIds, objectCode, contextId, filters }) => ({
        url: URLS.FRONT_API + `/data/gantChart/${configId}/timelines`,
        method: 'POST',
        body: { filters },
        params: { objectCode, contextId, timelineIds },
      }),
      extraOptions: {
        showNotification: false,
      },
    }),
    moveGanttBacklogBox: build.mutation<void, MoveBacklogGanttBoxArgs>({
      query: ({ configId, objectId, objectCode, resourceIdName, contextId }) => ({
        url: URLS.FRONT_API + `/data/gantChart/resourceShift/backlog/${configId}`,
        method: 'POST',
        params: { objectId, objectCode, resourceIdName, contextId },
      }),
    }),
    resizeGanttBox: build.mutation<void, ResizeGanttBoxArgs>({
      query: ({ configId, objectId, objectCode, timeValues, contextId }) => ({
        url: URLS.FRONT_API + `/data/gantChart/timeShift/${configId}`,
        method: 'POST',
        params: { objectId, objectCode, contextId },
        body: timeValues,
      }),
    }),
    moveGanttBox: build.mutation<void, MoveGanttBoxArgs>({
      query: ({ configId, objectId, objectCode, resourceIdValue, resourceIdName, contextId }) => ({
        url: URLS.FRONT_API + `/data/gantChart/resourceShift/${configId}`,
        method: 'POST',
        params: { objectId, objectCode, resourceIdValue, resourceIdName, contextId },
      }),
    }),
    fetchResourceData: build.mutation<FetchGanttChartDataResponse, FetchResourceDataArgs>({
      query: ({ configId, objectId, filters, contextId }) => ({
        url: URLS.FRONT_API + `/data/gantChart/${configId}/resource`,
        method: 'POST',
        body: { filters },
        params: { contextId, resourceIds: objectId },
      }),
      extraOptions: {
        showNotification: false,
      },
    }),
    executeCommand: build.mutation<void, ExtendedExecuteCommand>({
      query: ({ command, objectCode, objectId, ...body }) => ({
        url: URLS.FRONT_API + `/command/${objectCode}/${command}`,
        method: 'POST',
        params: { objectId, objectCode },
        body,
      }),
    }),
    fetchTimeWindowData: build.query<TimeWindowPeriod[], string>({
      query: chartId => ({
        url: URLS.FRONT_CFG + `/restriction/gantt/getByChartId/${chartId}`,
        method: 'GET',
      }),
      extraOptions: {
        showNotification: false,
      },
    }),
    setTimeWindow: build.mutation<TimeWindowPeriod[], TimeWindowPeriod[]>({
      query: body => ({
        url: URLS.FRONT_CFG + '/restriction/gantt',
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useMoveGanttBacklogBoxMutation,
  useResizeGanttBoxMutation,
  useMoveGanttBoxMutation,
  useFetchGanttChartDataQuery,
  useFetchGanttChartConfigQuery,
  useFetchGanttChartBacklogQuery,
  useFetchGanttChartQuery,
  useFetchTimeLineDataMutation,
  useFetchSeveralTimeLinesDataMutation,
  useFetchResourceDataMutation,
  useExecuteCommandMutation,
  useSetTimeWindowMutation,
  useFetchTimeWindowDataQuery,
} = ganttChartApi
